import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Switch } from "../../../components/ui";
import LanguageOptions from "../../../components/LanguageOptions";
import {
  QUILL_FORMATS,
  QUILL_MODULES,
} from "../../../components/editor/quillConfig";
import { readImageFile } from "../../../utils/files";
import "./ad-form.css";

export const AD_PLACEMENTS = ["home", "podcast", "tvshow", "video"];

const AUTOPLAY_OPTIONS = [
  { value: "instant", label: "Instant" },
  { value: "3 seconds", label: "3 seconds" },
  { value: "off", label: "Off" },
];

/** Form values of an existing ad (or empty ones for a new ad). */
export const adFormValues = (ad) => ({
  status: ad ? Boolean(ad.status) : false,
  title: ad?.title || "",
  description: ad?.description || "",
  redirect_url: ad?.redirect_url || "",
  language: ad?.default_language || "",
  language_code: ad?.language_code || "",
  placement: ad?.placement || [],
  autoplay: !ad?.autoplay || ad.autoplay === "0" ? "default" : ad.autoplay,
  expiry_date: ad?.expiry_date ? ad.expiry_date.split("T")[0] : "",
  // The API expects the current banner URL back when it is unchanged.
  imageBase64: ad?.banner_url || "",
});

/** Body of the create / update ad requests. */
export const adRequestBody = (values) => ({
  title: values.title,
  description: values.description,
  language_code: values.language_code,
  default_language: values.language,
  category: "ads",
  redirect_url: values.redirect_url,
  status: values.status,
  autoplay: values.autoplay === "default" ? "0" : values.autoplay,
  expiry_date: values.expiry_date,
  placement: values.placement,
  imageBase64: values.imageBase64,
});

/** Error message key for incomplete values, or null when they can be sent. */
export const adFormError = (values, { requireMedia, hasMedia } = {}) => {
  if (
    !values.title ||
    !values.description ||
    !values.redirect_url ||
    !values.expiry_date ||
    !values.language ||
    !values.language_code ||
    values.placement.length === 0
  ) {
    return "Please fill in all required fields.";
  }
  if (requireMedia && !hasMedia && !values.imageBase64) {
    return "Please upload either a banner image or a video.";
  }
  return null;
};

/**
 * Ad fields. In `edit` mode, also the status switch and the banner image.
 * `onSubmit(values)` performs the request; `hasMedia` tells whether a video
 * is attached (an ad needs a banner or a video to be saved).
 */
const AdForm = ({ initialValues, edit, hasMedia, onSubmit, submitLabel }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [bannerName, setBannerName] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (field) => (value) =>
    setValues((current) => ({ ...current, [field]: value }));
  const today = new Date().toISOString().split("T")[0];

  const togglePlacement = (place) =>
    set("placement")(
      values.placement.includes(place)
        ? values.placement.filter((p) => p !== place)
        : [...values.placement, place]
    );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const error = adFormError(values, { requireMedia: edit, hasMedia });
    if (error) {
      toast.error(t(error));
      return;
    }
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ui-card ad-form" onSubmit={handleSubmit}>
      <div className="ui-form ad-form-body">
        <div className="ui-field ui-field-full">
          <label htmlFor="ad-title">{t("Title")}</label>
          <input
            id="ad-title"
            className="ui-input"
            value={values.title}
            onChange={(e) => set("title")(e.target.value)}
          />
        </div>

        <div className="ui-field ui-field-full">
          <span className="ui-label">{t("desc")}</span>
          <ReactQuill
            className="ui-editor"
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            value={values.description}
            onChange={set("description")}
          />
        </div>

        <div className="ui-field ui-field-full">
          <label htmlFor="ad-url">{t("Redirect URL")}</label>
          <input
            id="ad-url"
            className="ui-input"
            type="url"
            placeholder="https://www.example.com"
            value={values.redirect_url}
            onChange={(e) => set("redirect_url")(e.target.value)}
          />
        </div>

        <div className="ui-field">
          <label htmlFor="ad-language">{t("Language")}</label>
          <select
            id="ad-language"
            className="ui-input"
            value={
              values.language
                ? `${values.language} ${values.language_code}`
                : "default"
            }
            onChange={(e) => {
              const [language, code] = e.target.value.split(" ");
              setValues((current) => ({
                ...current,
                language,
                language_code: code,
              }));
            }}
          >
            <option disabled value="default">
              {t("choose default lang")}
            </option>
            <option value="all all">All</option>
            <LanguageOptions />
          </select>
        </div>

        <div className="ui-field">
          <label htmlFor="ad-expiry">{t("Expiry Date")}</label>
          <input
            id="ad-expiry"
            className="ui-input"
            type="date"
            min={today}
            value={values.expiry_date}
            onKeyDown={(e) => e.preventDefault()}
            onChange={(e) => set("expiry_date")(e.target.value)}
          />
        </div>

        <div className="ui-field">
          <label htmlFor="ad-autoplay">{t("Autoplay")}</label>
          <select
            id="ad-autoplay"
            className="ui-input"
            value={values.autoplay}
            onChange={(e) => set("autoplay")(e.target.value)}
          >
            <option disabled value="default">
              {t("Auto play settings")}
            </option>
            {AUTOPLAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ui-field">
          <span className="ui-label">{t("Ads Placement")}</span>
          <div className="ui-chips">
            {AD_PLACEMENTS.map((place) => (
              <button
                key={place}
                type="button"
                className="ui-chip"
                aria-pressed={values.placement.includes(place)}
                onClick={() => togglePlacement(place)}
              >
                {place}
              </button>
            ))}
          </div>
        </div>

        {edit && (
          <>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("Banner")}</span>
              <div className="ad-banner">
                {values.imageBase64 ? (
                  <img src={values.imageBase64} alt="" />
                ) : (
                  <span className="ad-banner-empty" aria-hidden="true">
                    <i className="ri-image-line"></i>
                  </span>
                )}
                <label htmlFor="ad-banner" className="ad-upload">
                  <i className="ri-upload-2-line" aria-hidden="true"></i>
                  {bannerName || t("Upload Banner")}
                </label>
                <input
                  id="ad-banner"
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    setBannerName(file.name);
                    readImageFile(file, set("imageBase64"));
                  }}
                />
              </div>
            </div>
            <div className="ui-field ui-field-full ad-status">
              <div>
                <span className="ui-label">{t("status")}</span>
                <p className="ui-hint">
                  {t("An ad needs a banner or a video to be shown")}
                </p>
              </div>
              <Switch
                checked={values.status}
                onChange={set("status")}
                label={t("status")}
                onLabel={t("Active")}
                offLabel={t("Inactive")}
              />
            </div>
          </>
        )}
      </div>

      <div className="ad-form-footer">
        <Link to="/dashboard/ads-list" className="ui-btn ui-btn-secondary">
          {t("cancel")}
        </Link>
        <button type="submit" className="ui-btn" disabled={saving}>
          {saving && <span className="ad-spinner" aria-hidden="true" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default AdForm;
